/**
 * 主题解析与 Ant Design 桥接。
 *
 * 为什么需要一份 TS 镜像：AntD 的 ConfigProvider 只接受**字面色值**，
 * 它要拿 colorPrimary 做色彩运算派生出 hover/active/border 等十几个衍生色。
 * 传 `var(--x)` 进去，算法拿到的是字符串，派生色会整体失效。
 *
 * 所以这里镜像了 tokens.css 里的一小部分值（每主题 9 个）。镜像必然有漂移风险，
 * 对策是 `__tests__/theme.test.ts` 会解析 tokens.css 并逐个比对——
 * 改了 CSS 没改这里（或反过来）测试立刻红。
 *
 * **不要扩大镜像面积**。组件样式一律用 CSS 令牌，只有喂给 AntD 算法的种子值才放这儿。
 */

import type { ThemeConfig } from 'antd';
import { theme as antdTheme } from 'antd';

export type ThemeMode = 'light' | 'dark' | 'system';
export type ResolvedMode = 'light' | 'dark';

const STORAGE_KEY = 'aigzl.theme';

/** 与 tokens.css 对应的种子值。左侧注释是对应的令牌名，测试按此比对。 */
export const SEED = {
  light: {
    colorPrimary: '#4f46e5', // --state-accent-solid  (indigo-600)
    colorSuccess: '#059669', // --state-success-solid (emerald-600)
    colorWarning: '#f59e0b', // --state-warning-solid (amber-500)
    colorError: '#e11d48', // --state-danger-solid  (rose-600)
    colorInfo: '#0284c7', // --state-info-solid    (sky-600)
    colorTextBase: '#0f172a', // --fg-primary          (neutral-900)
    colorBgBase: '#ffffff', // --bg-surface          (neutral-0)
    colorBgLayout: '#f8fafc', // --bg-canvas           (neutral-50)
    colorBorder: '#cbd5e1', // --border-default      (neutral-300)
  },
  dark: {
    colorPrimary: '#5a58ec', // --state-accent-solid  (indigo-550)
    colorSuccess: '#10b981', // --state-success-solid (emerald-500)
    colorWarning: '#f59e0b', // --state-warning-solid (amber-500)
    colorError: '#f43f5e', // --state-danger-solid  (rose-500)
    colorInfo: '#0ea5e9', // --state-info-solid    (sky-500)
    colorTextBase: '#f1f5f9', // --fg-primary          (neutral-100)
    colorBgBase: '#0f172a', // --bg-surface          (neutral-900)
    colorBgLayout: '#020617', // --bg-canvas           (neutral-950)
    colorBorder: '#334155', // --border-default      (neutral-700)
  },
} as const;

/** 与 tokens.css 的几何令牌保持一致 */
const RADIUS = { md: 8, lg: 12 } as const;

export function resolveMode(mode: ThemeMode): ResolvedMode {
  if (mode !== 'system') return mode;
  // 'system' 在这里就解析掉，CSS 侧因此不需要 prefers-color-scheme 分支，
  // 也就不必把整套深色令牌复制一份（见 tokens.css 末尾说明）
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function readStoredMode(): ThemeMode {
  const v = localStorage.getItem(STORAGE_KEY);
  return v === 'light' || v === 'dark' || v === 'system' ? v : 'system';
}

export function storeMode(mode: ThemeMode): void {
  localStorage.setItem(STORAGE_KEY, mode);
}

/** 把解析后的主题写到 <html data-theme>，CSS 令牌据此切换 */
export function applyTheme(mode: ThemeMode): ResolvedMode {
  const resolved = resolveMode(mode);
  document.documentElement.setAttribute('data-theme', resolved);
  return resolved;
}

/** 监听系统主题变化。仅在 mode==='system' 时需要订阅。 */
export function watchSystemTheme(onChange: () => void): () => void {
  const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
  if (!mq) return () => {};
  mq.addEventListener('change', onChange);
  return () => mq.removeEventListener('change', onChange);
}

export function antdConfig(resolved: ResolvedMode): ThemeConfig {
  const s = SEED[resolved];
  return {
    algorithm: resolved === 'dark' ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
    token: {
      ...s,
      borderRadius: RADIUS.md,
      borderRadiusLG: RADIUS.lg,
      fontFamily:
        "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', " +
        "'Microsoft YaHei', 'Helvetica Neue', Arial, sans-serif",
      // 收紧默认间距：AntD 默认值偏松散，是"通用后台"观感的主要来源之一
      controlHeight: 32,
      paddingContentHorizontal: 12,
      wireframe: false,
    },
    components: {
      Layout: {
        headerBg: s.colorBgBase,
        siderBg: s.colorBgBase,
        bodyBg: s.colorBgLayout,
        headerHeight: 52,
        headerPadding: '0 16px',
      },
      Menu: {
        itemBorderRadius: RADIUS.md,
        itemMarginInline: 8,
        itemHeight: 36,
        itemMarginBlock: 2,
      },
      Card: { borderRadiusLG: RADIUS.lg },
      Table: { headerBg: s.colorBgLayout, borderRadiusLG: RADIUS.lg, cellPaddingBlock: 10 },
      Modal: { borderRadiusLG: RADIUS.lg },
      Drawer: { paddingLG: 16 },
      Tabs: { horizontalMargin: '0 0 12px 0' },
      Tag: { borderRadiusSM: RADIUS.md },
      Button: { primaryShadow: 'none', defaultShadow: 'none', dangerShadow: 'none' },
      Segmented: { borderRadius: RADIUS.md },
      Input: { paddingBlock: 5 },
      Select: { optionSelectedBg: resolved === 'dark' ? '#1e293b' : '#eef2ff' },
    },
  };
}
